/**
 * DraggableBlockPlugin — per-block drag handle + "+" insert menu.
 *
 * Drag mechanics (handle positioning, drop target line, reordering, zoom, file-
 * drag filtering) are delegated to Lexical 0.45's official
 * DraggableBlockPlugin_EXPERIMENTAL. We supply the menu UI (grip + "+"), a block-
 * insert picker (shared BLOCK_COMMANDS), and the drop-line element.
 */
import { $createParagraphNode, $getNearestNodeFromDOMNode } from 'lexical';
import { DraggableBlockPlugin_EXPERIMENTAL } from '@lexical/react/LexicalDraggableBlockPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Add, DragIndicator } from '@mui/icons-material';
import { BLOCK_COMMANDS, BlockCommand } from './blockCommands';
import classes from './DraggableBlockPlugin.module.scss';

const DRAGGABLE_MENU_CLASS = 'lex-draggable-block-menu';

function isOnMenu(element: HTMLElement): boolean {
  return !!element.closest(`.${DRAGGABLE_MENU_CLASS}`);
}

export function DraggableBlockPlugin({ anchorElem }: { anchorElem: HTMLElement }) {
  const [editor] = useLexicalComposerContext();
  const menuRef = useRef<HTMLDivElement>(null);
  const targetLineRef = useRef<HTMLDivElement>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [draggableElement, setDraggableElement] = useState<HTMLElement | null>(null);
  // Snapshot of the hovered block taken when "+" is clicked. The live
  // draggableElement gets cleared once the mouse moves onto the portaled picker,
  // so insertion must use this frozen reference instead.
  const insertTargetRef = useRef<HTMLElement | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Close the picker on outside click.
  useEffect(() => {
    if (!pickerOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        pickerRef.current &&
        !pickerRef.current.contains(target) &&
        addBtnRef.current &&
        !addBtnRef.current.contains(target)
      ) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [pickerOpen]);

  // Position the picker next to the "+" button, clamped to the viewport.
  useEffect(() => {
    if (!pickerOpen || !pickerRef.current || !addBtnRef.current) return;
    const btnRect = addBtnRef.current.getBoundingClientRect();
    const picker = pickerRef.current;
    const left = Math.max(
      4,
      Math.min(btnRect.right + 4, window.innerWidth - picker.offsetWidth - 4),
    );
    let top = btnRect.top;
    if (top + picker.offsetHeight > window.innerHeight - 4) {
      top = Math.max(4, window.innerHeight - picker.offsetHeight - 4);
    }
    picker.style.top = `${top}px`;
    picker.style.left = `${left}px`;
  }, [pickerOpen]);

  const insertBlock = useCallback(
    (cmd: BlockCommand) => {
      const target = insertTargetRef.current;
      if (!target) return;
      // Insert an empty paragraph after the hovered block and select it, then run
      // the command on that selection (mirrors the slash-menu behavior). Guard:
      // if the DOM node can't be mapped (it detached while the picker was open),
      // do NOT run the command — otherwise it would apply to the current
      // selection and insert the block in the wrong place.
      let inserted = false;
      editor.update(() => {
        const node = $getNearestNodeFromDOMNode(target);
        if (!node) return;
        const paragraph = $createParagraphNode();
        node.insertAfter(paragraph);
        paragraph.select();
        inserted = true;
      });
      if (inserted) cmd.run(editor);
      setPickerOpen(false);
    },
    [editor],
  );

  const menuComponent = (
    <div ref={menuRef} className={`${classes.menu} ${DRAGGABLE_MENU_CLASS}`}>
      <button
        ref={addBtnRef}
        type="button"
        data-testid="lexical-block-add"
        className={classes.addBtn}
        title="Insert block below"
        onClick={(e) => {
          e.stopPropagation();
          // Freeze the currently-hovered block before the picker opens.
          insertTargetRef.current = draggableElement;
          setPickerOpen((v) => !v);
        }}
      >
        <Add fontSize="small" />
      </button>
      <div className={classes.dragHandle} title="Drag to move">
        <DragIndicator fontSize="small" />
      </div>
    </div>
  );

  const targetLineComponent = <div ref={targetLineRef} className={classes.targetLine} />;

  const picker =
    pickerOpen &&
    createPortal(
      <div
        ref={pickerRef}
        data-testid="lexical-block-picker"
        className={`${classes.blockPicker} ${classes.blockPickerFixed}`}
      >
        <div className={classes.pickerHeader}>Insert block</div>
        <ul className={classes.pickerList}>
          {BLOCK_COMMANDS.map((cmd) => (
            <li key={cmd.key} className={classes.pickerItem} onClick={() => insertBlock(cmd)}>
              <span className={classes.pickerIcon}>{cmd.icon}</span>
              <span className={classes.pickerContent}>
                <span className={classes.pickerLabel}>{cmd.label}</span>
                <span className={classes.pickerDescription}>{cmd.description}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>,
      document.body,
    );

  return (
    <>
      <DraggableBlockPlugin_EXPERIMENTAL
        anchorElem={anchorElem}
        menuRef={menuRef}
        targetLineRef={targetLineRef}
        menuComponent={menuComponent}
        targetLineComponent={targetLineComponent}
        isOnMenu={isOnMenu}
        onElementChanged={setDraggableElement}
      />
      {picker}
    </>
  );
}
